import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { toast } from "sonner"
import { Star, X, Check, Loader2 } from "lucide-react"
import { submitAppReview } from "@/lib/supabase"

/**
 * Parcours d'avis, en deux temps.
 *
 * Temps 1 : la note, en un seul geste. Rien d'autre à l'écran — c'est ce qui fait la
 * différence entre un avis donné et un avis abandonné.
 *
 * Temps 2 : la suite dépend de la note. À partir de quatre étoiles on demande un texte
 * court publiable, avec consentement explicite et prénom. En dessous, on bascule sur un
 * message privé au coach : demander une publication à quelqu'un de mécontent est le
 * meilleur moyen de le mécontenter davantage, et le retour a plus de valeur en direct.
 *
 * Le composant est autonome pour pouvoir être déclenché d'ailleurs que des Réglages
 * (fin de bloc, record battu) : il ne dépend que de `open`, `onClose` et `source`.
 */

const PUBLIC_THRESHOLD = 4

interface Props {
  open: boolean
  onClose: () => void
  /** D'où le parcours a été déclenché — stocké tel quel avec l'avis. */
  source?: string
  defaultDisplayName?: string
}

export function ReviewFlow({ open, onClose, source = "settings", defaultDisplayName = "" }: Props) {
  const [rating, setRating] = useState<number | null>(null)
  const [hover, setHover] = useState<number | null>(null)
  const [comment, setComment] = useState("")
  const [consent, setConsent] = useState(false)
  const [displayName, setDisplayName] = useState(defaultDisplayName)
  const [saving, setSaving] = useState(false)

  const reset = () => {
    setRating(null); setHover(null); setComment(""); setConsent(false); setSaving(false)
  }

  const close = () => {
    if (saving) return
    reset()
    onClose()
  }

  const isPublic = rating !== null && rating >= PUBLIC_THRESHOLD

  const submit = async () => {
    if (rating == null || saving) return
    setSaving(true)
    try {
      await submitAppReview({
        rating,
        comment: comment.trim() || null,
        // Le consentement ne peut jamais être vrai sur la branche privée, même si l'état
        // avait été coché avant un changement de note.
        publish_consent: isPublic && consent,
        display_name: isPublic && consent ? (displayName.trim() || null) : null,
        source,
      })
      toast.success(isPublic ? "Merci, ton avis est envoyé !" : "Merci, ton message est parti au coach.")
      reset()
      onClose()
    } catch (err) {
      toast.error("Envoi échoué", { description: err instanceof Error ? err.message : undefined })
      setSaving(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-[4px]"
            onClick={close}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className="fixed z-[100] left-4 right-4 top-1/2 -translate-y-1/2 max-w-[420px] mx-auto p-6 rounded-[22px]"
            style={{ background: "linear-gradient(180deg,#0f1829,#0a1020)", border: "1px solid rgba(30,41,59,0.55)" }}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-[16px] font-bold text-white">
                  {rating == null ? "Tu recommanderais Zeyone ?" : isPublic ? "Merci ! Tu veux en dire un mot ?" : "Qu'est-ce qui coince ?"}
                </h3>
                <p className="text-[12px] text-gray-500 mt-1 leading-relaxed">
                  {rating == null
                    ? "Une note, c'est tout — deux secondes."
                    : isPublic
                      ? "Ton mot pourra être affiché sur le site si tu l'autorises."
                      : "Ce message part directement au coach, il n'est jamais publié."}
                </p>
              </div>
              <button
                onClick={close}
                aria-label="Fermer"
                className="w-8 h-8 rounded-lg grid place-items-center cursor-pointer hover:bg-white/[0.04] shrink-0"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Temps 1 — la note */}
            <div className="flex items-center justify-center gap-2 py-2" onMouseLeave={() => setHover(null)}>
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = (hover ?? rating ?? 0) >= n
                return (
                  <button
                    key={n}
                    type="button"
                    aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                    onMouseEnter={() => setHover(n)}
                    onClick={() => setRating(n)}
                    className="p-1.5 cursor-pointer transition-transform active:scale-90"
                  >
                    <Star
                      className="w-8 h-8 transition-colors"
                      style={{ color: filled ? "#f59e0b" : "#334155" }}
                      fill={filled ? "#f59e0b" : "none"}
                    />
                  </button>
                )
              })}
            </div>

            {/* Temps 2 — la suite dépend de la note */}
            {rating != null && (
              <div className="mt-4">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  maxLength={400}
                  autoFocus
                  placeholder={isPublic ? "Ce qui t'a le plus servi…" : "Dis-moi ce qui ne va pas, je lis tout."}
                  className="w-full px-3.5 py-3 rounded-xl text-[13.5px] text-white outline-none resize-none placeholder:text-gray-600"
                  style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)", caretColor: "#06b6d4" }}
                />

                {isPublic && (
                  <>
                    <label className="flex items-start gap-2.5 mt-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-cyan-500 cursor-pointer shrink-0"
                      />
                      <span className="text-[12px] text-gray-400 leading-relaxed">
                        J'autorise la publication de mon avis avec mon prénom.
                      </span>
                    </label>

                    {consent && (
                      <input
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        maxLength={40}
                        placeholder="Prénom affiché"
                        className="w-full mt-2.5 px-3.5 h-11 rounded-xl text-[13.5px] text-white outline-none placeholder:text-gray-600"
                        style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)", caretColor: "#06b6d4" }}
                      />
                    )}
                  </>
                )}

                <button
                  onClick={submit}
                  disabled={saving}
                  className="w-full mt-4 py-3 rounded-xl text-[14px] font-bold text-white cursor-pointer disabled:opacity-70 flex items-center justify-center gap-2"
                  style={{ background: "linear-gradient(135deg,#0369a1,#06b6d4)" }}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {isPublic ? "Envoyer mon avis" : "Envoyer au coach"}
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
